import React from 'react';
import { useTranslation } from 'react-i18next';

import { Arrow } from 'icons/untitledui';

import * as Text from 'theme/components/Text.style';
import * as Styled from 'priorities/styles/InlinePriorities.style';

export interface InlinePrioritiesSummaryProps {
  routineItems: string[];
  eventUpdateItems: string[];
  onViewAll: () => void;
  isActive?: boolean;
}

const MAX_VISIBLE_ITEMS = 5;

function SummarySection({
  title,
  items,
}: {
  title: string;
  items: string[];
}): JSX.Element {
  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS);
  return (
    <Styled.SummaryCard data-testid="inline-priorities-summary-section">
      <Text.P2 as="h3">{`${title} (${items.length})`}</Text.P2>
      <Styled.SummaryList>
        {visibleItems.map((item, index) => (
          <Styled.SummaryListItem key={`${title}-${index}`}>
            <Styled.SummaryListItemText>{item}</Styled.SummaryListItemText>
          </Styled.SummaryListItem>
        ))}
      </Styled.SummaryList>
    </Styled.SummaryCard>
  );
}

export default function InlinePrioritiesSummary({
  routineItems,
  eventUpdateItems,
  onViewAll,
  isActive = false,
}: InlinePrioritiesSummaryProps): JSX.Element | null {
  const { t } = useTranslation('pagePriorities');
  const totalCount = routineItems.length + eventUpdateItems.length;

  if (totalCount === 0) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onViewAll();
    }
  };

  return (
    <Styled.Container
      data-testid="inline-priorities-summary"
      role="button"
      tabIndex={0}
      onClick={onViewAll}
      onKeyDown={handleKeyDown}
      aria-label={t('inlinePriorities.viewAllAriaLabel')}
      $isActive={isActive}
    >
      <Styled.Header>
        <Text.P3Bold as="h2">
          {`${t('inlinePriorities.title')} (${totalCount})`}
        </Text.P3Bold>
        <Styled.HeaderChevron aria-hidden>
          <Arrow.ChevronRight />
        </Styled.HeaderChevron>
      </Styled.Header>
      <Styled.SummaryGrid>
        {routineItems.length > 0 && (
          <SummarySection
            title={t('inlinePriorities.sections.routine')}
            items={routineItems}
          />
        )}
        {eventUpdateItems.length > 0 && (
          <SummarySection
            title={t('inlinePriorities.sections.updateEvents')}
            items={eventUpdateItems}
          />
        )}
      </Styled.SummaryGrid>
    </Styled.Container>
  );
}
